<template>
    <div class="relative pb-6">
        <div class="bg-gray-50 rounded-lg shadow-md space-y-6 divide-y divide-gray-200">
            <div class="border border-gray-200 space-y-6 bg-white rounded-md shadow-sm">
                <div class="sm:col-span-6 flex justify-between items-center border-b border-gray-200 py-4 px-4">
                    <div>
                        <h4 class="text-xl font-bold text-gray-700 sm:text-2xl sm:truncate">Financial Documents</h4>
                        <p class="text-sm text-gray-500">Please upload your financial documents as requested by the landlord/agent</p>
                    </div>
                </div>
                <div class="p-4 pt-0">
                    <div class="sm:col-span-6 sm:flex justify-between items-center pl-4">
                        <div>
                            <h5 class="text-lg font-bold text-gray-700">Upload Instructions</h5>
                            <p class="text-sm text-gray-500">Please include the following documents depending on your situation</p>
                        </div>
                    </div>
                    <ul class="list-decimal ml-8 space-y-1 text-sm py-4">
                        <li>Government Photo ID <span class="text-red-500 font-bold">(Required)</span></li>
                        <li>If W-2 employee, upload 3 most recent pay stubs</li>
                        <li>If accepting a new job, upload offer letter</li>
                        <li>If self-employed without W-2, upload 3 most recent bank statements (please redact account number) and last two years tax returns (signature page and page showing adjusted gross income only)</li>
                        <li>
                            If military:
                            
                            <ul class="list-disc ml-5 mt-1">
                                <li>Upload most recent LES</li>
                                <li>Upload a copy of your orders</li>
                            </ul>
                        </li>
                        <li>
                            Upload one document at a time
                        </li>
                        <li>
                            When you have uploaded all documents click the checkbox and Done button below
                        </li>
                    </ul>

                    <form class="space-y-6 p-6 border border-gray-200 rounded-md" @submit.prevent="update">
                        <div class="space-y-8">
                        <!-- File Input and Checkbox Row -->
                            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                <!-- File Input -->
                                <div class="sm:col-span-6 md:col-span-3">
                                    <label for="doc_file" :class="form.errors.doc_file ? 'block text-sm font-medium text-red-500' : 'block text-sm font-medium text-gray-700'">
                                        Choose File <span class="text-red-700">*</span>
                                    </label>
                                    <div class="mt-2">
                                        <input id="doc_file" @input="handleFileChange" :error="form.errors.doc_file" type="file"
                                            :class="form.errors.doc_file ? 'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border border-red-300 p-1 rounded-md' : 'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border border-gray-300 p-1 rounded-md'">
                                        <p v-text="form.errors.doc_file" class="text-red-500 text-sm"></p>
                                        <progress v-if="form.progress" :value="form.progress.percentage" max="100" class="mt-2 block w-full">
                                            {{ form.progress.percentage }}%
                                        </progress>
                                    </div>
                                </div>

                                <!-- Government Photo ID Checkbox -->
                                <div class="sm:col-span-6 md:col-span-3 flex items-center pt-6">
                                    <label class="text-sm font-medium text-gray-700 flex items-center space-x-2">
                                        <input type="checkbox" v-model="form.is_government_photo_id" class="rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400">
                                        <span>Is this document the Government Photo ID?</span>
                                    </label>
                                </div>
                            </div>

                            <!-- Acknowledgment Checkbox -->
                            <div class="sm:col-span-6 mt-4">
                                <label class="text-sm font-medium text-gray-700 flex items-center space-x-2">
                                    <input type="checkbox" v-model="form.acknowledgment_shared_with_agent" class="rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400">
                                    <span>I acknowledge that the information provided in these documents will be shared with the landlord or agent.</span>
                                </label>
                                <p v-text="form.errors.acknowledgment_shared_with_agent" class="text-red-500 text-sm mt-1"></p>
                            </div>
                        </div>
                        
                        <div class="justify-start flex mt-3-important">
                            <form-button :form="form" type="submit" :disabled="isUploadDisabled" class="ml-0">
                                Upload Document
                            </form-button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        <div class="border border-gray-200 space-y-6 bg-white rounded-md shadow-sm mt-6">
            <div class="sm:col-span-6 flex justify-between items-center border-b border-gray-200 py-4 px-4">
                <div>
                    <h4 class="text-xl font-bold text-gray-700 sm:text-2xl sm:truncate">My Financial Documents</h4>
                    <p class="text-sm text-gray-500">Below is a list of recently uploaded financial documents</p>
                </div>
            </div>
            <div class="p-6 pt-0">
                <div class="flow-root">
                    <div class="inline-block min-w-full align-middle">
                        <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                            <table class="min-w-full divide-y divide-gray-300">
                                <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Filename</th>
                                        <th scope="col" class="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Download</th>
                                        <th scope="col" class="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Delete</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-200">
                                    <tr v-for="(document, index) in documents" :key="document.id" :class="[(index % 2 === 0) ? 'bg-gray-50 border-b' : 'bg-white','hover:bg-yellow-50']">
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">
                                            {{ document.file_name }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6 text-center">
                                            <a :href="route('application.download-financial-document', document)" target="_blank" class="text-blue-600 hover:text-blue-800">
                                                <font-awesome-icon icon="fa-download" />
                                            </a>
                                        </td>
                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                            <button type="button" v-on:click="deleteDoc(document)" class="font-medium text-red-600 hover:text-red-400">
                                                <font-awesome-icon icon="fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-if="documents.length === 0">
                                        <td colspan="3" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">
                                        No financial documents found.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <span class="text-sm flex justify-end text-red-700 mt-2">*Files older than 6 months will be deleted automatically</span>
            </div>
        </div>
        <div class="border border-gray-200 space-y-6 bg-white rounded-md shadow-sm mt-6">
            <div class="sm:col-span-6 flex justify-between items-center border-b border-gray-200 py-4 px-4">
                <div>
                    <h4 class="text-xl font-bold text-gray-700 sm:text-2xl sm:truncate">Done Uploading Financial Documents</h4>
                    <p class="text-sm text-gray-500">Thank you for uploading your financial documents.</p>
                </div>
            </div>
            <div class="p-6 pt-0">
                <p class="text-sm">I am done uploading documents, or I did not have any documents to upload.</p>
                <p class="text-sm">Mark the Financial Documents Step Complete</p>
                <div class="justify-start flex mt-3">
                    <form-button class="ml-0" :form="form2" type="button" @click="complete">
                        Complete
                    </form-button>
                </div>
            </div>
        </div>
    </div>
    <div v-if="showDeleteDocModal">
        <TransitionRoot as="template" :show="showDeleteDocModal">
            <Dialog as="div" class="relative z-50" @close="showDeleteDocModal = false">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                        <font-awesome-icon :icon="faTrash" class="h-6 w-6 text-red-500" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to delete this financial document?</DialogTitle>
                                        <div class="mt-2">
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex">
                                    <button type="button" class="whiteButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold hover:bg-gray-500 hover:text-white focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="showDeleteDocModal = false">Cancel</button>
                                    <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="submitDeleteDoc()">Confirm</button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script>
import FormButton from '@/Shared/FormButton.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import SelectInput from "@/Shared/SelectInput.vue"
import Button from "@/Jetstream/Button.vue";
import Label from "@/Jetstream/Label.vue";
import Input from "@/Jetstream/Input.vue";
import AlertCard from "@/Shared/AlertCard.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from "@headlessui/vue";

export default {
    components: {
        AlertCard,
        Button,
        Input,
        Label,
        FormButton,
        LoadingButton,
        SelectInput,
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        FontAwesomeIcon
    },
    props: {
        property: Object,
        applicant: Object,
        states: Object,
        application: Object,
        documents: Object,
        experian_attempt: Object,
        step_completion_status: Object,
        step_progress_data: Object,
        completed_steps_map: Object,
    },
    data() {
        return {
            faTrash,
            form: this.$inertia.form({
                user_id: this.application.user_id,
                id: this.application.id,
                doc_file: null,
                is_government_photo_id: false,
                acknowledgment_shared_with_agent: false,
            }),
            form2: this.$inertia.form({
                user_id: this.application.user_id,
                id: this.application.id,
                is_no_documents: this.application.is_no_documents,
            }),
            showDeleteDocModal: false,
            docToBeDeleted: null,
            isUploadDisabled: true,
        }
    },
    methods: {
        update() {
            this.form.post(this.route('application.update-financial-documents', this.application.id), {
                onSuccess: () => {
                    this.form.reset();
                    this.isUploadDisabled = true;
                }
            })
        },
        complete() {
            this.form.post(this.route('application.complete-financial-documents', this.application.id))
        },
        deleteDoc(doc) {
            this.docToBeDeleted = doc;
            this.showDeleteDocModal = true;
        },
        download(doc) {
            this.form.get(this.route('application.download-financial-document', doc))
        },
        submitDeleteDoc() {
            this.form.get(this.route('application.delete-financial-document', this.docToBeDeleted), {
                onFinish: () => {
                    this.showDeleteDocModal = false;
                    this.docToBeDeleted = null;
                }
            })
        },
        handleFileChange(event) {
            const selectedFile = event.target.files[0];
            if (selectedFile) {
                const maxSizeInBytes = 10 * 1000 * 1000; // 10 MB
                if (selectedFile.size > maxSizeInBytes) {
                    this.form.errors.doc_file = "File size exceeds the maximum allowed size of 10 MB.";
                    this.isUploadDisabled = true;
                } else {
                    this.isUploadDisabled = false;
                    this.form.doc_file = selectedFile;
                    this.form.errors.doc_file = null; // Clear error if file size is within limit
                }
            } else {
                this.isUploadDisabled = true;
                this.form.errors.doc_file = "Please select a file.";
            }
        },
    },
}
</script>